import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import * as d3 from 'd3';
import { TriangleAlert } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import oroborosGraphLogo from '../assets/oroboros_logo_graph.png';
import graphBackgroundPattern from '../assets/patterns/dots_6_percent_512px.png';
import { DATA_TYPE_META, resolveTraceColor } from '../stores/graphStore.js';
import { useNavigationStore } from '../stores/navigationStore.js';
import MarkInfoTooltip from './MarkInfoTooltip';
import './graph.css';

// Module-scope d3 bisectors — stateless, safe to share across instances.
const TIME_FIELD_BISECTOR = d3.bisector((d) => d.time).left;
const TIME_SCALAR_BISECTOR = d3.bisector((d) => d).left;

const DEFAULT_X_TRANSFORM = { k: 1, translate: 0 };

/**
 * ChamberPlotView — D3 Canvas+SVG chart driven by per-graph trace data.
 * Pure renderer: data + graph settings come in as props; the only store it
 * touches is navigationStore for x-axis sync between plots in the same tab.
 */
const ChamberPlotView = ({
  height = '100%',
  controlsRef,
  chamber = 'A',
  isActive = false,
  // Per-trace resolved data: [{ trace, time:Date[], value:number[] }].
  // Empty array → plot renders only the x-axis.
  traceData = [],
  marks = [],
  events = [],
  graph = null,
  isLoading = false,
  error = null,
  tabId = null,
  onSyncXTransform = null,
  onSyncXDomainOverride = null,
  // Called on mousemove with {time, y1, y2} or null when the cursor leaves.
  onCursorChange = null,
  // [startMs, endMs] — fallback time extent for graphs with no traces, so
  // their baseXScale matches chambers' and the same xTransform yields the
  // same visible window.
  referenceTimeExtentMs = null,
}) => {
  const syncKey = tabId || '__legacy';
  const syncXTransform =
    useNavigationStore(useCallback((state) => state.tabXTransforms[syncKey], [syncKey])) ??
    DEFAULT_X_TRANSFORM;
  const syncXDomainOverride =
    useNavigationStore(useCallback((state) => state.tabXDomainOverrides[syncKey], [syncKey])) ??
    null;
  const isLiveSyncing = useNavigationStore((state) => state.isLiveSyncing);
  const setIsLiveSyncing = useNavigationStore((state) => state.setIsLiveSyncing);

  const setSyncXTransform = useCallback(
    (transform) => {
      const key = tabId || '__legacy';
      useNavigationStore.getState().setSyncXTransform(key, transform);
      onSyncXTransform?.(transform);
    },
    [tabId, onSyncXTransform],
  );

  const setSyncXDomainOverride = useCallback(
    (domain) => {
      const key = tabId || '__legacy';
      useNavigationStore.getState().setSyncXDomainOverride(key, domain);
      onSyncXDomainOverride?.(domain);
    },
    [tabId, onSyncXDomainOverride],
  );

  const resetSyncNavigation = useCallback(() => {
    if (tabId) {
      useNavigationStore.getState().resetTabNavigation(tabId);
    } else {
      useNavigationStore.getState().resetSyncNavigation();
    }
  }, [tabId]);

  const currentMarks = marks;
  const currentEvents = events;

  const selectedTrace = useMemo(() => {
    if (!graph?.traces?.length) return null;
    return graph.traces.find((t) => t.id === graph.selectedTraceId) ?? graph.traces[0];
  }, [graph?.traces, graph?.selectedTraceId]);

  const y1TraceData = useMemo(() => traceData.filter((td) => td.trace.yAxis === 'y1'), [traceData]);
  const y2TraceData = useMemo(() => traceData.filter((td) => td.trace.yAxis === 'y2'), [traceData]);
  const hasFluxData = y2TraceData.length > 0;

  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const svgRef = useRef(null);
  const markGradientIdRef = useRef(`mark-gradient-${Math.random().toString(36).slice(2)}`);
  const theme = useTheme();

  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // X-transform is synced globally across plots; Y-transforms stay local.
  const [xTransform, setXTransform] = useState(() => syncXTransform);
  const [yTransform, setYTransform] = useState({ k: 1, translate: 0 });
  const [fluxYTransform, setFluxYTransform] = useState({ k: 1, translate: 0 });
  const [xDomainOverride, setXDomainOverride] = useState(() => syncXDomainOverride);
  const [yDomainOverride, setYDomainOverride] = useState(null);
  const [fluxYDomainOverride, setFluxYDomainOverride] = useState(null);
  const [hoveredMark, setHoveredMark] = useState(null);

  const rafIdRef = useRef(0);
  const xTransformRef = useRef({ k: 1, translate: 0 });
  const yTransformRef = useRef({ k: 1, translate: 0 });
  const fluxYTransformRef = useRef({ k: 1, translate: 0 });
  const yDomainOverrideRef = useRef(null);
  const fluxYDomainOverrideRef = useRef(null);
  const xDomainOverrideRef = useRef(null);
  const panLockRef = useRef(null); // null | 'x' (vertical only) | 'y' (horizontal only)
  const zoomEndRafRef = useRef(0);
  const renderSVGRef = useRef(null);
  const isDraggingRef = useRef(false);
  const pendingXTransformRef = useRef(null);
  const pendingYTransformRef = useRef(null);
  const pendingFluxYTransformRef = useRef(null);
  const pendingYDomainOverrideRef = useRef(null);
  const pendingFluxYDomainOverrideRef = useRef(null);
  const lastAxisUpdateRef = useRef(0);
  const updateSVGAxesRef = useRef(null);
  const dragStartRef = useRef({
    x: 0,
    y: 0,
    xTransform: { k: 1, translate: 0 },
    yTransform: { k: 1, translate: 0 },
    fluxYTransform: { k: 1, translate: 0 },
    yDomainOverride: null,
    fluxYDomainOverride: null,
  });

  const zoomTransitionRef = useRef(null);
  const zoomTargetRef = useRef(null);

  // Pull the current store transform into local state when this plot becomes
  // active — passive plots update via refs only, so React state can be stale.
  const prevIsActiveRef = useRef(isActive);
  useEffect(() => {
    if (isActive && !prevIsActiveRef.current) {
      const storeTransform =
        useNavigationStore.getState().tabXTransforms[tabId || '__legacy'] || DEFAULT_X_TRANSFORM;
      setXTransform(storeTransform);
      xTransformRef.current = storeTransform;
    }
    prevIsActiveRef.current = isActive;
  }, [isActive, tabId]);

  // Packed time array (longest trace) used for x-extent + bisector lookups.
  const data = useMemo(() => {
    if (!traceData.length) return [];
    let longest = traceData[0];
    for (const td of traceData) {
      if ((td.time?.length ?? 0) > (longest.time?.length ?? 0)) longest = td;
    }
    const n = longest.time?.length ?? 0;
    if (!n) return [];
    const result = new Array(n);
    for (let i = 0; i < n; i++) {
      result[i] = { time: longest.time[i], oxygen: longest.value[i] ?? 0 };
    }
    return result;
  }, [traceData]);

  const margin = useMemo(() => ({ top: 24, right: 90, bottom: 60, left: 100 }), []);
  const plotWidth = Math.max(0, dimensions.width - margin.left - margin.right);
  const plotHeight = Math.max(0, dimensions.height - margin.top - margin.bottom);

  const graphScaling = graph?.scaling ?? null;
  const graphAdvanced = graph?.advanced ?? null;

  // baseXScale always uses the full data extent (or the parent-provided
  // reference extent when the graph has no traces) so that xTransforms shared
  // via navigationStore produce the same visible window across all graphs.
  // The dialog-set crop is applied via xTransform, not by clamping the base.
  const baseXScale = useMemo(() => {
    let domain = xDomainOverride;
    if (!domain && data.length) {
      domain = d3.extent(data, (d) => d.time);
    }
    if (!domain && Array.isArray(referenceTimeExtentMs)) {
      domain = [new Date(referenceTimeExtentMs[0]), new Date(referenceTimeExtentMs[1])];
    }
    if (
      !domain &&
      graphScaling &&
      typeof graphScaling.xMin === 'number' &&
      typeof graphScaling.xMax === 'number'
    ) {
      domain = [new Date(graphScaling.xMin * 1000), new Date(graphScaling.xMax * 1000)];
    }
    if (!domain) domain = [new Date(), new Date()];
    return d3.scaleUtc().domain(domain).range([0, plotWidth]);
  }, [data, plotWidth, xDomainOverride, graphScaling, referenceTimeExtentMs]);

  // Union of value extents across a list of traceData entries; null if empty.
  const unionExtent = (tdList) => {
    let lo = Infinity;
    let hi = -Infinity;
    for (const td of tdList) {
      const arr = td.value;
      if (!Array.isArray(arr)) continue;
      for (let i = 0; i < arr.length; i++) {
        const v = arr[i];
        if (Number.isFinite(v)) {
          if (v < lo) lo = v;
          if (v > hi) hi = v;
        }
      }
    }
    return Number.isFinite(lo) && Number.isFinite(hi) ? [lo, hi] : null;
  };

  // y1 base scale. Domain priority: pan/zoom override → dialog-set bounds →
  // union of y1 trace values (5% pad) → [0,1] placeholder when axis is empty.
  const baseYScale = useMemo(() => {
    if (yDomainOverride) {
      return d3.scaleLinear().domain(yDomainOverride).range([plotHeight, 0]);
    }
    if (
      graphScaling &&
      typeof graphScaling.y1Min === 'number' &&
      typeof graphScaling.y1Max === 'number'
    ) {
      return d3
        .scaleLinear()
        .domain([graphScaling.y1Min, graphScaling.y1Max])
        .nice()
        .range([plotHeight, 0]);
    }
    const ext = unionExtent(y1TraceData);
    if (!ext) return d3.scaleLinear().domain([0, 1]).range([plotHeight, 0]);
    const pad = Math.max(1e-6, (ext[1] - ext[0]) * 0.05);
    return d3
      .scaleLinear()
      .domain([ext[0] - pad, ext[1] + pad])
      .nice()
      .range([plotHeight, 0]);
  }, [plotHeight, yDomainOverride, graphScaling, y1TraceData]);

  // y2 base scale. Same priority order as y1.
  const baseFluxYScale = useMemo(() => {
    if (fluxYDomainOverride) {
      return d3.scaleLinear().domain(fluxYDomainOverride).range([plotHeight, 0]);
    }
    if (
      graphScaling &&
      typeof graphScaling.y2Min === 'number' &&
      typeof graphScaling.y2Max === 'number'
    ) {
      return d3
        .scaleLinear()
        .domain([graphScaling.y2Min, graphScaling.y2Max])
        .nice()
        .range([plotHeight, 0]);
    }
    const ext = unionExtent(y2TraceData);
    if (!ext) return d3.scaleLinear().domain([0, 1]).range([plotHeight, 0]);
    const pad = Math.max(1e-6, (ext[1] - ext[0]) * 0.05);
    return d3
      .scaleLinear()
      .domain([ext[0] - pad, ext[1] + pad])
      .nice()
      .range([plotHeight, 0]);
  }, [plotHeight, fluxYDomainOverride, graphScaling, y2TraceData]);

  // Transformed scales for rendering (using separate x and y transforms)
  const xScale = useMemo(() => {
    const [d0, d1] = baseXScale.domain();
    const [r0, r1] = baseXScale.range();
    // Apply scale and translate: new_domain = (old_domain - translate/k) / k
    const k = xTransform.k;
    const tx = xTransform.translate;
    const newD0 = baseXScale.invert((baseXScale(d0) - tx) / k);
    const newD1 = baseXScale.invert((baseXScale(d1) - tx) / k);
    return d3.scaleUtc().domain([newD0, newD1]).range([r0, r1]);
  }, [baseXScale, xTransform]);

  // Transformed Y scale for oxygen
  const yScale = useMemo(() => {
    const [d0, d1] = baseYScale.domain();
    const [r0, r1] = baseYScale.range();
    const k = yTransform.k;
    const ty = yTransform.translate;
    const newD0 = baseYScale.invert((baseYScale(d0) - ty) / k);
    const newD1 = baseYScale.invert((baseYScale(d1) - ty) / k);
    return d3.scaleLinear().domain([newD0, newD1]).range([r0, r1]);
  }, [baseYScale, yTransform]);

  // Transformed Y scale for flux
  const fluxYScale = useMemo(() => {
    const [d0, d1] = baseFluxYScale.domain();
    const [r0, r1] = baseFluxYScale.range();
    const k = fluxYTransform.k;
    const ty = fluxYTransform.translate;
    const newD0 = baseFluxYScale.invert((baseFluxYScale(d0) - ty) / k);
    const newD1 = baseFluxYScale.invert((baseFluxYScale(d1) - ty) / k);
    return d3.scaleLinear().domain([newD0, newD1]).range([r0, r1]);
  }, [baseFluxYScale, fluxYTransform]);

  // Marks/events feeding the SVG render loop. Filtered by trace selection
  // and advanced flags but NOT by viewport — viewport visibility is handled
  // by the SVG clip-path. These memos are what determines which DOM elements
  // exist; visibleMarks/visibleEvents below are viewport-clipped subsets used
  // only for position-update bookkeeping during pan/zoom.
  const marksForSvg = useMemo(() => {
    if (graphAdvanced && graphAdvanced.showMarks === false) return [];
    if (!Array.isArray(currentMarks) || !currentMarks.length) return [];
    if (!selectedTrace) return [];
    const channel = selectedTrace.channel;
    return currentMarks.filter((m) => !m.mark_signal_type || m.mark_signal_type === channel);
  }, [currentMarks, graphAdvanced, selectedTrace]);

  // Drop the hovered mark whenever the visible mark set changes OR the chamber
  // becomes inactive — prevents a stale tooltip after the user toggles a trace
  // radio that hides marks, or after switching the active chamber. Also
  // cancels any pending linger timer so a hidden/inactive mark doesn't pop up.
  useEffect(() => {
    setHoveredMark(null);
    pendingHoveredMarkRef.current = null;
    if (markHoverTimerRef.current) {
      clearTimeout(markHoverTimerRef.current);
      markHoverTimerRef.current = 0;
    }
  }, [marksForSvg, isActive]);

  // Ref-mirror of marksForSvg for the mousemove hit-test inside the event
  // handler effect (which doesn't re-attach on every marksForSvg change).
  const marksForSvgRef = useRef(marksForSvg);
  useEffect(() => {
    marksForSvgRef.current = marksForSvg;
  }, [marksForSvg]);

  // Pending mark-tooltip timer + the candidate mark/position it's holding.
  // Tooltip only commits to state after the user lingers on a mark for
  // MARK_TOOLTIP_DELAY_MS — keeps tooltips quiet while skimming dense data.
  const MARK_TOOLTIP_DELAY_MS = 200;
  const markHoverTimerRef = useRef(0);
  const pendingHoveredMarkRef = useRef(null);

  useEffect(() => {
    return () => {
      if (markHoverTimerRef.current) {
        clearTimeout(markHoverTimerRef.current);
        markHoverTimerRef.current = 0;
      }
    };
  }, []);

  const eventsForSvg = useMemo(() => {
    if (graphAdvanced && graphAdvanced.showEvents === false) return [];
    if (!Array.isArray(currentEvents) || !currentEvents.length) return [];
    const hideInstrument =
      graphAdvanced && graphAdvanced.showInstrumentAndNotificationEvents === false;
    if (!hideInstrument) return currentEvents;
    return currentEvents.filter((e) => {
      const t = (e.type || '').toString().toUpperCase();
      return t !== 'MACHINE' && t !== 'NOTIFICATION';
    });
  }, [currentEvents, graphAdvanced]);

  // Filter marks to only those visible in viewport, AND only those whose
  // signal type matches the selected trace's channel. If no trace is selected
  // or the selected trace's channel has no associated marks, none render.
  // Respects graph.advanced.showMarks (master toggle).
  const visibleMarks = useMemo(() => {
    if (graphAdvanced && graphAdvanced.showMarks === false) return [];
    if (!Array.isArray(currentMarks) || !currentMarks.length) return [];
    if (!selectedTrace) return [];
    const channel = selectedTrace.channel;
    const [xMin, xMax] = xScale.domain();
    const xMinMs = xMin.getTime();
    const xMaxMs = xMax.getTime();

    return currentMarks.filter((m) => {
      if (m.mark_signal_type && m.mark_signal_type !== channel) return false;
      const startMs = new Date(m.start).getTime();
      const endMs = new Date(m.end ?? m.start).getTime();
      return !(endMs < xMinMs || startMs > xMaxMs);
    });
  }, [currentMarks, xScale, graphAdvanced, selectedTrace]);

  // Filter events to only those visible in viewport (for real-time updates).
  // Respects graph.advanced.showEvents (master toggle) and
  // graph.advanced.showInstrumentAndNotificationEvents (filter for MACHINE/NOTIFICATION types).
  const visibleEvents = useMemo(() => {
    if (graphAdvanced && graphAdvanced.showEvents === false) return [];
    if (!Array.isArray(currentEvents) || !currentEvents.length) return [];
    const [xMin, xMax] = xScale.domain();
    const xMinMs = xMin.getTime();
    const xMaxMs = xMax.getTime();

    const hideInstrumentEvents =
      graphAdvanced && graphAdvanced.showInstrumentAndNotificationEvents === false;

    const visible = currentEvents.filter((e) => {
      const eventTime = e.time instanceof Date ? e.time : new Date(e.time);
      const eventMs = eventTime.getTime();
      if (eventMs < xMinMs || eventMs > xMaxMs) return false;
      if (hideInstrumentEvents) {
        const t = (e.type || '').toString().toUpperCase();
        if (t === 'MACHINE' || t === 'NOTIFICATION') return false;
      }
      return true;
    });

    return visible;
  }, [currentEvents, xScale, graphAdvanced]);

  // Flag to skip reading our own writes (set during our store updates)
  const skipNextSyncRef = useRef(false);
  const lastSyncedXTransformRef = useRef(syncXTransform);
  // rAF handle for passive graph live sync
  const liveSyncRafRef = useRef(0);

  // Sync X-axis transform from global store to local state
  // Only update when the store value actually changes (not from our own writes)
  // Skip syncing during live sync (handled by subscription above) and during drag for active plot
  // Sync xTransform from the store. Compares against lastSyncedXTransformRef
  // (not React state) so the per-chamber local-seed below — which sets state
  // without writing to the store — isn't clobbered. Post-pan stale state on
  // passive plots is handled by the separate reconciliation effect below.
  useEffect(() => {
    if (isActive && isDraggingRef.current) return;
    if (!isActive && isLiveSyncing) return;
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      return;
    }
    const lastSynced = lastSyncedXTransformRef.current;
    if (
      Math.abs(syncXTransform.k - lastSynced.k) > 1e-9 ||
      Math.abs(syncXTransform.translate - lastSynced.translate) > 1e-3
    ) {
      setXTransform(syncXTransform);
      xTransformRef.current = syncXTransform;
      lastSyncedXTransformRef.current = syncXTransform;
    }
  }, [syncXTransform, isActive, isLiveSyncing]);

  // Passive-plot post-pan reconciliation: during live sync the rAF
  // subscription updates xTransformRef and the canvas but skips React state.
  // When live sync ends, pull the ref into React state. Gated by a ref so it
  // fires exactly once per live-sync session — keeping `xTransform` out of
  // deps avoids the setState/dep-loop class that triggers "Maximum update
  // depth" when many plots resubscribe simultaneously (e.g. on Apply).
  const reconciledRef = useRef(false);
  useEffect(() => {
    if (isLiveSyncing) {
      reconciledRef.current = false;
      return;
    }
    if (isActive || reconciledRef.current) return;
    reconciledRef.current = true;
    setXTransform(xTransformRef.current);
  }, [isLiveSyncing, isActive]);

  useEffect(() => {
    if (isActive && isDraggingRef.current) return;
    if (syncXDomainOverride !== xDomainOverrideRef.current) {
      setXDomainOverride(syncXDomainOverride);
      xDomainOverrideRef.current = syncXDomainOverride;
    }
  }, [syncXDomainOverride, isActive]);

  // Time-axis crop seeding. Each plot converts graph.scaling.xMin/xMax (in
  // seconds) into its own xTransform using its own plotWidth — runs for
  // every plot (active and passive) so all share the same visible window.
  // Pan/zoom does NOT write graph.scaling; it writes the visible window to
  // navigationStore.tabVisibleWindows. The dialog reads/writes graph.scaling.
  const firstTraceLen = traceData[0]?.time?.length ?? 0;
  const firstTraceStartMs = traceData[0]?.time?.[0]?.getTime() ?? 0;
  const fileIdentity = firstTraceLen ? `${firstTraceLen}:${firstTraceStartMs}` : null;

  // (xMin, xMax) the seed was last applied for. Matching bounds → seed is a
  // no-op (preserves user's pan/zoom across remounts/tab-switches). On
  // mount, if navigationStore holds a non-default xTransform, treat the
  // current bounds as already seeded so the user's prior pan position wins.
  const lastSeededBoundsRef = useRef(undefined);
  if (lastSeededBoundsRef.current === undefined) {
    const stored = tabId ? useNavigationStore.getState().tabXTransforms[tabId] : null;
    lastSeededBoundsRef.current =
      stored && (stored.k !== 1 || stored.translate !== 0)
        ? { xMin: graphScaling?.xMin, xMax: graphScaling?.xMax }
        : null;
  }

  useEffect(() => {
    if (!data.length || plotWidth <= 0) return;
    if (!graphScaling) return;
    const { xMin, xMax } = graphScaling;
    if (typeof xMin !== 'number' || typeof xMax !== 'number') return;
    const lastSeeded = lastSeededBoundsRef.current;
    if (lastSeeded && lastSeeded.xMin === xMin && lastSeeded.xMax === xMax) return;

    const baseDomain = d3.extent(data, (d) => d.time);
    if (!baseDomain[0] || !baseDomain[1]) return;
    const tempBase = d3.scaleUtc().domain(baseDomain).range([0, plotWidth]);
    const pMin = tempBase(new Date(xMin * 1000));
    const pMax = tempBase(new Date(xMax * 1000));
    if (!Number.isFinite(pMin) || !Number.isFinite(pMax) || pMax - pMin <= 0) return;
    const k = plotWidth / (pMax - pMin);
    const translate = -k * pMin;
    setXTransform({ k, translate });
    xTransformRef.current = { k, translate };
    lastSeededBoundsRef.current = { xMin, xMax };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphScaling?.xMin, graphScaling?.xMax, fileIdentity, plotWidth]);

  // Keep refs in sync with state for the zoom/pan handlers.
  useEffect(() => {
    xTransformRef.current = xTransform;
  }, [xTransform]);
  useEffect(() => {
    yTransformRef.current = yTransform;
  }, [yTransform]);
  useEffect(() => {
    fluxYTransformRef.current = fluxYTransform;
  }, [fluxYTransform]);
  useEffect(() => {
    yDomainOverrideRef.current = yDomainOverride;
  }, [yDomainOverride]);
  useEffect(() => {
    fluxYDomainOverrideRef.current = fluxYDomainOverride;
  }, [fluxYDomainOverride]);
  useEffect(() => {
    xDomainOverrideRef.current = xDomainOverride;
  }, [xDomainOverride]);

  // ── Canvas rendering & mark positioning ──────────────────────────

  // Viewport-clipped + downsampled index range for a single trace.
  const computeTraceIndices = (xScale, traceTime, plotWidth) => {
    if (!traceTime?.length || plotWidth <= 0) return { startIdx: 0, endIdx: 0, step: 1 };
    const [xMin, xMax] = xScale.domain();
    const startIdx = Math.max(0, TIME_SCALAR_BISECTOR(traceTime, xMin) - 1);
    const endIdx = Math.min(traceTime.length, TIME_SCALAR_BISECTOR(traceTime, xMax) + 1);
    const visibleLen = endIdx - startIdx;
    const maxPoints = plotWidth * 2;
    const step = visibleLen > maxPoints ? Math.ceil(visibleLen / maxPoints) : 1;
    return { startIdx, endIdx, step };
  };

  // Helper: Render canvas with all visible traces.
  // Each trace draws against y1 or y2 scale based on trace.yAxis.
  const drawCanvasTrace = useCallback(
    (canvas, xScale, yScale, fluxYScale) => {
      if (!canvas || plotWidth <= 0 || plotHeight <= 0) return;

      const ctx = canvas.getContext('2d');
      const DPR = window.devicePixelRatio || 1;

      canvas.width = Math.max(1, Math.floor(dimensions.width * DPR));
      canvas.height = Math.max(1, Math.floor(dimensions.height * DPR));
      canvas.style.width = `${dimensions.width}px`;
      canvas.style.height = `${dimensions.height}px`;

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(DPR, DPR);
      ctx.clearRect(0, 0, dimensions.width, dimensions.height);

      if (!traceData.length) return;

      ctx.save();
      ctx.beginPath();
      ctx.rect(margin.left, margin.top, plotWidth, plotHeight);
      ctx.clip();
      ctx.translate(margin.left, margin.top);

      for (const td of traceData) {
        const yScaleForTrace = td.trace.yAxis === 'y2' ? fluxYScale : yScale;
        if (!yScaleForTrace) continue;
        const color = resolveTraceColor(td.trace.color);
        const { startIdx, endIdx, step } = computeTraceIndices(xScale, td.time, plotWidth);
        if (endIdx <= startIdx) continue;

        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        let first = true;
        for (let i = startIdx; i < endIdx; i += step) {
          const x = xScale(td.time[i]);
          const y = yScaleForTrace(td.value[i]);
          if (Number.isFinite(x) && Number.isFinite(y)) {
            if (first) {
              ctx.moveTo(x, y);
              first = false;
            } else {
              ctx.lineTo(x, y);
            }
          }
        }
        // Always include last point
        if (endIdx - 1 > startIdx) {
          const lastIdx = endIdx - 1;
          const x = xScale(td.time[lastIdx]);
          const y = yScaleForTrace(td.value[lastIdx]);
          if (Number.isFinite(x) && Number.isFinite(y)) ctx.lineTo(x, y);
        }
        ctx.stroke();
      }

      ctx.restore();
    },
    [dimensions.width, dimensions.height, plotWidth, plotHeight, margin, traceData],
  );

  // Helper: Update mark positions in SVG
  const updateMarkPositions = useCallback((xScale, visibleMarkKeys) => {
    try {
      const svg = d3.select(svgRef.current);
      const g = svg.select('g');
      const marksLayer = g.select('.marks-layer');
      const marksTopLayer = g.select('.marks-top-layer');

      if (marksLayer.empty() || marksTopLayer.empty()) return;

      // Update body marks
      marksLayer.selectAll('g.mark').each(function () {
        const el = d3.select(this);
        const start = Number(el.attr('data-start'));
        const end = Number(el.attr('data-end')) || start;
        const x1 = xScale(new Date(start));
        const x2 = xScale(new Date(end));
        const left = Math.min(x1, x2);
        const width = Math.max(1, Math.abs(x2 - x1));

        // Update rectangle
        el.select('rect').attr('x', left).attr('width', width);

        // Update left and right vertical lines explicitly
        // Select lines by their position (first is left, second is right)
        const lines = el.selectAll('line').nodes();
        if (lines.length >= 2) {
          // Left line (first line)
          d3.select(lines[0]).attr('x1', left).attr('x2', left);
          // Right line (second line)
          d3.select(lines[1])
            .attr('x1', left + width)
            .attr('x2', left + width);
        }

        // Update label position
        el.select('text.mark-label').attr('x', left + 4);
      });

      // Update top badges
      marksTopLayer.selectAll('rect.mark-top').each(function () {
        const el = d3.select(this);
        const start = Number(el.attr('data-start'));
        const end = Number(el.attr('data-end')) || start;
        const x1 = xScale(new Date(start));
        const x2 = xScale(new Date(end));
        const left = Math.min(x1, x2);
        const width = Math.max(2, Math.abs(x2 - x1));
        el.attr('x', left).attr('width', width);
      });
    } catch {}
  }, []);

  // Helper: Update event positions in SVG
  const updateEventPositions = useCallback((xScale, visibleEventKeys) => {
    try {
      const svg = d3.select(svgRef.current);
      const g = svg.select('g');
      const eventsLayer = g.select('.events-layer');

      if (eventsLayer.empty()) return;

      // Update event lines
      eventsLayer.selectAll('line.event-line').each(function () {
        const el = d3.select(this);
        const eventTime = Number(el.attr('data-time'));
        const eventX = xScale(new Date(eventTime));

        if (Number.isFinite(eventX)) {
          el.attr('x1', eventX).attr('x2', eventX);
        }
      });

      // Update event labels (centered on the line) and their backgrounds
      eventsLayer.selectAll('g.event-label-group').each(function () {
        const group = d3.select(this);
        const eventTime = Number(group.attr('data-time'));
        const eventX = xScale(new Date(eventTime));
        if (Number.isFinite(eventX)) {
          const textEl = group.select('text.event-label');
          textEl.attr('x', eventX);

          // Update background rectangle position
          const bbox = textEl.node().getBBox();
          const padding = 4;
          group
            .select('rect.event-label-bg')
            .attr('x', bbox.x - padding)
            .attr('y', bbox.y - padding / 2)
            .attr('width', bbox.width + padding * 2)
            .attr('height', bbox.height + padding);
        }
      });
    } catch {}
  }, []);

  // Subscription-based sync for passive graphs during live panning
  // This updates refs and redraws canvas via rAF WITHOUT triggering React re-renders
  // Placed here because it depends on drawCanvasTrace, updateMarkPositions, updateEventPositions
  useEffect(() => {
    // Only passive graphs need this subscription (active graph drives the sync)
    if (isActive) return;

    const syncKey = tabId || '__legacy';
    const unsubscribe = useNavigationStore.subscribe(
      (state) => ({
        transform: state.tabXTransforms[syncKey] || { k: 1, translate: 0 },
        isLive: state.isLiveSyncing,
      }),
      (current, previous) => {
        const { transform, isLive } = current;
        // Only handle updates during live syncing
        if (!isLive) return;

        // Skip our own writes
        if (skipNextSyncRef.current) {
          skipNextSyncRef.current = false;
          return;
        }

        // Check if transform actually changed
        const lastSynced = lastSyncedXTransformRef.current;
        if (transform.k === lastSynced.k && transform.translate === lastSynced.translate) {
          return;
        }

        // Update refs directly (no React state update = no re-render)
        xTransformRef.current = transform;
        lastSyncedXTransformRef.current = transform;

        // Schedule canvas redraw via rAF
        cancelAnimationFrame(liveSyncRafRef.current);
        liveSyncRafRef.current = requestAnimationFrame(() => {
          // Recompute scales and redraw canvas
          const canvas = canvasRef.current;
          if (!canvas || plotWidth <= 0 || plotHeight <= 0 || !data.length) return;

          // Compute X scale from transform
          const [d0, d1] = baseXScale.domain();
          const k = transform.k;
          const tx = transform.translate;
          const newXd0 = baseXScale.invert((baseXScale(d0) - tx) / k);
          const newXd1 = baseXScale.invert((baseXScale(d1) - tx) / k);
          const tempXScale = d3.scaleUtc().domain([newXd0, newXd1]).range(baseXScale.range());

          // Y scales (use current refs for any domain overrides)
          let tempBaseYScale = baseYScale;
          if (yDomainOverrideRef.current) {
            tempBaseYScale = d3
              .scaleLinear()
              .domain(yDomainOverrideRef.current)
              .range([plotHeight, 0]);
          }
          const yK = yTransformRef.current.k;
          const yTy = yTransformRef.current.translate;
          const [yd0, yd1] = tempBaseYScale.domain();
          const newYd0 = tempBaseYScale.invert((tempBaseYScale(yd0) - yTy) / yK);
          const newYd1 = tempBaseYScale.invert((tempBaseYScale(yd1) - yTy) / yK);
          const tempYScale = d3
            .scaleLinear()
            .domain([newYd0, newYd1])
            .range(tempBaseYScale.range());

          let tempFluxYScale = null;
          if (hasFluxData) {
            let tempBaseFluxYScale = baseFluxYScale;
            if (fluxYDomainOverrideRef.current) {
              tempBaseFluxYScale = d3
                .scaleLinear()
                .domain(fluxYDomainOverrideRef.current)
                .range([plotHeight, 0]);
            }
            const fluxK = fluxYTransformRef.current.k;
            const fluxTy = fluxYTransformRef.current.translate;
            const [fd0, fd1] = tempBaseFluxYScale.domain();
            const newFd0 = tempBaseFluxYScale.invert((tempBaseFluxYScale(fd0) - fluxTy) / fluxK);
            const newFd1 = tempBaseFluxYScale.invert((tempBaseFluxYScale(fd1) - fluxTy) / fluxK);
            tempFluxYScale = d3
              .scaleLinear()
              .domain([newFd0, newFd1])
              .range(tempBaseFluxYScale.range());
          }

          // Compute visible data and draw canvas
          drawCanvasTrace(canvas, tempXScale, tempYScale, tempFluxYScale);

          // Update mark and event positions
          updateMarkPositions(tempXScale, new Set());
          updateEventPositions(tempXScale, new Set());

          // Update axes
          if (updateSVGAxesRef.current) {
            updateSVGAxesRef.current(tempXScale, tempYScale, tempFluxYScale);
          }
        });
      },
      {
        equalityFn: (a, b) =>
          a.transform.k === b.transform.k &&
          a.transform.translate === b.transform.translate &&
          a.isLive === b.isLive,
      },
    );

    return () => {
      unsubscribe();
      cancelAnimationFrame(liveSyncRafRef.current);
    };
  }, [
    isActive,
    baseXScale,
    baseYScale,
    baseFluxYScale,
    plotWidth,
    plotHeight,
    data,
    hasFluxData,
    drawCanvasTrace,
    updateMarkPositions,
    updateEventPositions,
  ]);

  // ResizeObserver → drives pixel ranges for scales/axes
  // Re-attach when plot container mounts after being conditionally rendered
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDimensions({ width: Math.round(width), height: Math.round(height) });
    });
    // Initialize immediately to avoid a frame with width/height = 0
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      setDimensions({ width: Math.round(rect.width), height: Math.round(rect.height) });
    }
    ro.observe(el);
    return () => ro.disconnect();
  }, [traceData, isLoading]);

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDERING - Canvas & SVG
  // ═══════════════════════════════════════════════════════════════════════════

  // Canvas rendering (used by wheel zoom path via useEffect)
  const renderCanvas = useCallback(() => {
    drawCanvasTrace(canvasRef.current, xScale, yScale, fluxYScale);
  }, [xScale, yScale, fluxYScale, drawCanvasTrace]);

  // Lightweight update: Only update axes/grids/labels without rebuilding entire SVG
  const updateSVGAxes = useCallback(
    (xScale, yScale, fluxYScale) => {
      const svgEl = svgRef.current;
      if (!svgEl) return;

      const svg = d3.select(svgEl);
      const g = svg.select('g');
      if (g.empty()) return; // SVG not initialized yet

      // Update X-axis
      const xAxis = d3.axisBottom(xScale).tickFormat(d3.utcFormat('%H:%M:%S'));
      const xAxisGroup = g.select('.x-axis');
      if (!xAxisGroup.empty()) {
        xAxisGroup.call(xAxis);
        xAxisGroup.selectAll('.tick text').attr('dy', '12px');
      }

      // Update Y-axes (left shows oxygen, right shows flux if available)
      const yAxis = d3.axisLeft(yScale).ticks(3);
      const yAxisGroups = g.selectAll('.y-axis');
      yAxisGroups.each(function (d, i) {
        const el = d3.select(this);
        if (i === 0) {
          // Left axis - oxygen
          el.call(yAxis);
          el.selectAll('.tick text').attr('dx', '-4px');
        } else if (i === 1 && fluxYScale) {
          // Right axis - flux
          const fluxYAxisRight = d3.axisRight(fluxYScale).ticks(3);
          el.call(fluxYAxisRight);
          el.selectAll('.tick text').attr('dx', '4px');
        } else {
          // Fallback: right axis shows oxygen if no flux
          const yAxisRight = d3.axisRight(yScale).ticks(3);
          el.call(yAxisRight);
          el.selectAll('.tick text').attr('dx', '4px');
        }
      });

      // Update visible range label — formatted in the graph's configured xUnit.
      const visStart = xScale.invert(0);
      const visEnd = xScale.invert(plotWidth);
      const xUnit = graphScaling?.xUnit ?? 'hh:mm:ss';
      let durationLabel = '';
      if (visStart && visEnd) {
        const totalSeconds = Math.floor(Math.abs(visEnd - visStart) / 1000);
        if (xUnit === 'seconds') {
          durationLabel = String(totalSeconds);
        } else {
          const hh = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
          const mm = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
          const ss = String(totalSeconds % 60).padStart(2, '0');
          durationLabel = xUnit === 'hh:mm' ? `${hh}:${mm}` : `${hh}:${mm}:${ss}`;
        }
      }
      const rangeLabel = svg.select('.visible-range-label');
      if (!rangeLabel.empty()) {
        rangeLabel.text(`${durationLabel} visible range [${xUnit}]`);
      }

      // Update X-grid
      const xGrid = d3.axisBottom(xScale).tickSize(-plotHeight).tickFormat('').ticks(10);
      const xGridGroup = g.select('.x-grid');
      if (!xGridGroup.empty()) {
        xGridGroup.call(xGrid);
      }

      // Update Y-grid (rebuild custom grid lines at tick positions only)
      const yGridGroup = g.select('.y-grid-custom');
      if (!yGridGroup.empty()) {
        yGridGroup.selectAll('line').remove();
        // Get tick values from the Y-axis (matches the 3 ticks we configured)
        const yAxisTemp = d3.axisLeft(yScale).ticks(3);
        const tickValues = yAxisTemp.scale().ticks ? yAxisTemp.scale().ticks(3) : yScale.ticks(3);

        tickValues.forEach((v) => {
          const yPos = yScale(v);
          if (Number.isFinite(yPos) && yPos >= 0 && yPos <= plotHeight) {
            yGridGroup
              .append('line')
              .attr('x1', 0)
              .attr('y1', yPos)
              .attr('x2', plotWidth)
              .attr('y2', yPos);
          }
        });
      }
    },
    [plotWidth, plotHeight, graphScaling?.xUnit],
  );

  // SVG axes + labels (no cursor visuals here)
  const renderSVG = useCallback(() => {
    const svgEl = svgRef.current;
    if (!svgEl) return;

    const svg = d3.select(svgEl);
    svg.selectAll('*').remove();

    svg
      .attr('width', dimensions.width)
      .attr('height', dimensions.height)
      .style('user-select', 'none')
      .style('cursor', 'default');

    const clipId = 'plot-clip-id';
    const groupClipId = 'plot-group-clip-id';
    const topClipId = 'plot-top-clip-id';
    const defs = svg.append('defs');

    defs
      .append('clipPath')
      .attr('id', clipId)
      .append('rect')
      .attr('width', plotWidth)
      .attr('height', plotHeight)
      .attr('x', margin.left)
      .attr('y', margin.top);

    // Clip-path for elements rendered within the translated group (local coords)
    defs
      .append('clipPath')
      .attr('id', groupClipId)
      .append('rect')
      .attr('width', plotWidth)
      .attr('height', plotHeight)
      .attr('x', 0)
      .attr('y', 0);

    // Clip-path for top badges: restrict horizontally to plot and allow a small band above
    const topBand = 12; // pixels above plot to allow for badge drawing
    defs
      .append('clipPath')
      .attr('id', topClipId)
      .append('rect')
      .attr('width', plotWidth)
      .attr('height', topBand)
      .attr('x', 0)
      .attr('y', -topBand);

    svg
      .append('rect')
      .attr('class', 'base-background')
      .attr('width', plotWidth)
      .attr('height', plotHeight)
      .attr('x', margin.left)
      .attr('y', margin.top)
      .attr('clip-path', `url(#${clipId})`);

    // Oroboros Logo, centered in the plot
    svg
      .append('image')
      .attr('xlink:href', oroborosGraphLogo)
      .attr('width', 290)
      .attr('height', 282)
      .attr('x', margin.left + plotWidth / 2 - 290 / 2)
      .attr('y', margin.top + plotHeight / 2 - 282 / 2)
      .attr('clip-path', `url(#${clipId})`);

    const pattern = defs
      .append('pattern')
      .attr('id', 'background-pattern')
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('width', 512)
      .attr('height', 512);
    // Gradient for marks fill
    const markGradient = defs
      .append('linearGradient')
      .attr('id', markGradientIdRef.current)
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '0%')
      .attr('y2', '100%');
    markGradient
      .append('stop')
      .attr('offset', '0%')
      .attr('stop-color', '#CB1045')
      .attr('stop-opacity', 0.13);
    markGradient
      .append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#FFAAC3')
      .attr('stop-opacity', 0.05);

    pattern
      .append('image')
      .attr('xlink:href', graphBackgroundPattern)
      .attr('width', 512)
      .attr('height', 512);

    svg
      .append('rect')
      .attr('width', plotWidth)
      .attr('height', plotHeight)
      .attr('x', margin.left)
      .attr('y', margin.top)
      .attr('fill', 'url(#background-pattern)')
      .attr('clip-path', `url(#${clipId})`);

    const xAxis = d3.axisBottom(xScale).tickFormat(d3.utcFormat('%H:%M:%S'));
    const yAxis = d3.axisLeft(yScale).ticks(3);
    const yAxisRight = d3.axisRight(yScale).ticks(3);

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    g.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${plotHeight})`)
      .call(xAxis)
      .selectAll('.tick text')
      .attr('dy', '12px');

    g.append('g').attr('class', 'y-axis').call(yAxis).selectAll('.tick text').attr('dx', '-4px');

    // Right Y-axis: use flux scale if available, otherwise oxygen scale
    const rightYAxis = hasFluxData && fluxYScale ? d3.axisRight(fluxYScale).ticks(3) : yAxisRight;
    g.append('g')
      .attr('class', 'y-axis')
      .attr('transform', `translate(${plotWidth},0)`)
      .call(rightYAxis)
      .selectAll('.tick text')
      .attr('dx', '4px');

    // Y-axis labels — derived from the first trace on each axis. Empty when
    // the axis has no traces (no misleading "O₂ concentration" placeholder).
    const labelForAxisTrace = (td) => {
      if (!td) return '';
      const meta = DATA_TYPE_META[td.trace.channel];
      const name = meta?.label ?? td.trace.channel;
      const unit = meta?.unit ? ` [${meta.unit}]` : '';
      return `${name}${unit}`;
    };
    g.append('text')
      .attr('class', 'y-axis-label')
      .attr('transform', `translate(${-margin.left + 24}, ${plotHeight / 2}) rotate(-90)`)
      .attr('text-anchor', 'middle')
      .text(labelForAxisTrace(y1TraceData[0]));

    g.append('text')
      .attr('class', 'y-axis-label')
      .attr(
        'transform',
        `translate(${plotWidth + margin.right - 4}, ${plotHeight / 2}) rotate(-90)`,
      )
      .attr('text-anchor', 'middle')
      .text(labelForAxisTrace(y2TraceData[0]));

    // Visible time span label centered below the plot
    const visStart = xScale.invert(0);
    const visEnd = xScale.invert(plotWidth);
    const xUnit = graphScaling?.xUnit ?? 'hh:mm:ss';
    let durationLabel = '';
    if (visStart && visEnd) {
      const totalSeconds = Math.floor(Math.abs(visEnd - visStart) / 1000);
      if (xUnit === 'seconds') {
        durationLabel = String(totalSeconds);
      } else {
        const hh = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
        const mm = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
        const ss = String(totalSeconds % 60).padStart(2, '0');
        durationLabel = xUnit === 'hh:mm' ? `${hh}:${mm}` : `${hh}:${mm}:${ss}`;
      }
    }

    svg
      .append('text')
      .attr('class', 'visible-range-label')
      .attr('x', margin.left + plotWidth / 2)
      .attr('y', margin.top + plotHeight + margin.bottom - 12)
      .attr('text-anchor', 'middle')
      .text(`${durationLabel} visible range [${xUnit}]`);

    const xGrid = d3.axisBottom(xScale).tickSize(-plotHeight).tickFormat('').ticks(10);

    g.append('g')
      .attr('class', 'x-grid')
      .attr('transform', `translate(0,${plotHeight})`)
      .call(xGrid);

    const yGridGroup = g.append('g').attr('class', 'y-grid-custom');
    // Get tick values from the Y-axis (matches the 3 ticks we configured)
    const tickValues = yScale.ticks(3);

    tickValues.forEach((v) => {
      const yPos = yScale(v);
      if (Number.isFinite(yPos) && yPos >= 0 && yPos <= plotHeight) {
        yGridGroup
          .append('line')
          .attr('x1', 0)
          .attr('y1', yPos)
          .attr('x2', plotWidth)
          .attr('y2', yPos);
      }
    });

    // Render marks layer (behind overlay)
    const marksLayer = g
      .append('g')
      .attr('class', 'marks-layer')
      .attr('clip-path', `url(#${groupClipId})`);
    // Layer for top badges outside of plot area (clipped to a top band)
    const marksTopLayer = g
      .append('g')
      .attr('class', 'marks-top-layer')
      .attr('clip-path', `url(#${topClipId})`);

    if (Array.isArray(marksForSvg) && marksForSvg.length > 0) {
      marksForSvg.forEach((m, idx) => {
        try {
          const startDate = m.start instanceof Date ? m.start : new Date(m.start);
          const endDate = m.end instanceof Date ? (m.end ?? m.start) : new Date(m.end ?? m.start);
          const x1 = xScale(startDate);
          const x2 = xScale(endDate);
          const left = Math.min(x1, x2);
          const width = Math.max(1, Math.abs(x2 - x1));

          const startMs = startDate.getTime();
          const endMs = endDate.getTime();
          const group = marksLayer
            .append('g')
            .attr('class', 'mark')
            .attr('data-start', startMs)
            .attr('data-end', endMs)
            .attr('data-index', idx);
          group
            .append('rect')
            .attr('x', left)
            .attr('y', 0)
            .attr('width', width)
            .attr('height', plotHeight)
            .attr('fill', `url(#${markGradientIdRef.current})`);

          // Left and right stroke lines
          group
            .append('line')
            .attr('x1', left)
            .attr('x2', left)
            .attr('y1', 0)
            .attr('y2', plotHeight)
            .attr('stroke', '#F5426A')
            .attr('stroke-width', 1);
          group
            .append('line')
            .attr('x1', left + width)
            .attr('x2', left + width)
            .attr('y1', 0)
            .attr('y2', plotHeight)
            .attr('stroke', '#F5426A')
            .attr('stroke-width', 1);

          const labelText = m.name ?? m.label;
          if (labelText) {
            group
              .append('text')
              .attr('class', 'mark-label')
              .attr('x', left + 4)
              .attr('y', plotHeight - 6)
              .attr('fill', '#F5426A')
              .text(String(labelText));
          }

          // rectangular badge on top outside the plot area
          const topWidth = Math.max(2, width);
          const topHeight = 4;
          const topX = left;
          const topY = -topHeight;
          marksTopLayer
            .append('rect')
            .attr('class', 'mark-top')
            .attr('data-start', startMs)
            .attr('data-end', endMs)
            .attr('data-index', idx)
            .attr('x', topX)
            .attr('y', topY)
            .attr('width', topWidth)
            .attr('height', topHeight)
            .attr('fill', '#F5426A')
            .attr('stroke', '#F5426A')
            .attr('opacity', 1);
        } catch {}
      });
    }

    // Render events layer (black vertical lines for single time point events)
    const eventsLayer = g
      .append('g')
      .attr('class', 'events-layer')
      .attr('clip-path', `url(#${groupClipId})`);

    if (Array.isArray(eventsForSvg) && eventsForSvg.length > 0) {
      eventsForSvg.forEach((e, idx) => {
        try {
          const eventTime = e.time instanceof Date ? e.time : new Date(e.time);
          if (isNaN(eventTime.getTime())) return;

          const eventX = xScale(eventTime);
          if (!Number.isFinite(eventX)) return;

          const eventMs = eventTime.getTime();

          // Vertical line for event (black)
          eventsLayer
            .append('line')
            .attr('class', 'event-line')
            .attr('data-time', eventMs)
            .attr('data-index', idx)
            .attr('x1', eventX)
            .attr('x2', eventX)
            .attr('y1', 0)
            .attr('y2', plotHeight)
            .attr('stroke', '#000000')
            .attr('stroke-width', 1)
            .attr('opacity', 0.7);

          // Event name label centered on the line, positioned slightly down from top
          if (e.name) {
            const labelGroup = eventsLayer
              .append('g')
              .attr('class', 'event-label-group')
              .attr('data-time', eventMs)
              .attr('data-index', idx);

            // Create text first to measure it
            const textNode = labelGroup
              .append('text')
              .attr('class', 'event-label')
              .attr('x', eventX)
              .attr('y', 16)
              .attr('fill', '#000000')
              .attr('font-size', '10px')
              .attr('opacity', 0.9)
              .attr('text-anchor', 'middle')
              .text(String(e.name));

            // Get text bounding box to size the background (with small delay to ensure rendering)
            try {
              const bbox = textNode.node().getBBox();
              const padding = 4;

              // Insert background rectangle before text so it renders behind
              labelGroup
                .insert('rect', 'text')
                .attr('class', 'event-label-bg')
                .attr('x', bbox.x - padding)
                .attr('y', bbox.y - padding / 2)
                .attr('width', bbox.width + padding * 2)
                .attr('height', bbox.height + padding)
                .attr('fill', '#FFFFFF')
                .attr('opacity', 0.9)
                .attr('stroke', 'none');
            } catch {
              // Fallback: use estimated size if bbox not available yet
              const estimatedWidth = e.name.length * 6; // Rough estimate: ~6px per character
              const estimatedHeight = 12;
              const padding = 4;
              labelGroup
                .insert('rect', 'text')
                .attr('class', 'event-label-bg')
                .attr('x', eventX - estimatedWidth / 2 - padding)
                .attr('y', 14 - estimatedHeight / 2 - padding / 2)
                .attr('width', estimatedWidth + padding * 2)
                .attr('height', estimatedHeight + padding)
                .attr('fill', '#FFFFFF')
                .attr('opacity', 0.9)
                .attr('stroke', 'none');
            }
          }
        } catch {}
      });
    }

    // Overlay rect for pointer-events (no D3 event handlers - handled at root SVG)
    g.append('rect')
      .attr('class', 'overlay')
      .attr('width', plotWidth)
      .attr('height', plotHeight)
      .style('fill', 'none')
      .style('pointer-events', 'all')
      .style('cursor', 'default');
  }, [
    dimensions.width,
    dimensions.height,
    margin,
    plotWidth,
    plotHeight,
    xScale,
    yScale,
    fluxYScale,
    hasFluxData,
    data,
    marksForSvg,
    eventsForSvg,
    graphScaling?.xUnit,
    y1TraceData,
    y2TraceData,
  ]);

  // Keep stable references to render functions for use in event handlers
  useEffect(() => {
    renderSVGRef.current = renderSVG;
  }, [renderSVG]);

  useEffect(() => {
    updateSVGAxesRef.current = updateSVGAxes;
  }, [updateSVGAxes]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ZOOM & PAN BEHAVIOR - Custom implementation with axis-specific zoom
  // ═══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const svgEl = svgRef.current;
    if (!svgEl || plotWidth <= 0 || plotHeight <= 0) return;

    const svg = d3.select(svgEl);

    // Helper: Detect which region mouse is over
    const getRegion = (clientX, clientY) => {
      const rect = svgEl.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      const withinPlotX = x >= margin.left && x <= margin.left + plotWidth;
      const withinPlotY = y >= margin.top && y <= margin.top + plotHeight;
      const inXAxisBand =
        withinPlotX && y >= margin.top + plotHeight && y <= margin.top + plotHeight + margin.bottom;
      const inYLeftBand = x >= 0 && x <= margin.left && withinPlotY;
      const inYRightBand = x >= margin.left + plotWidth && x <= dimensions.width && withinPlotY;
      const inPlot = withinPlotX && withinPlotY;

      if (inXAxisBand) return 'x-axis';
      if (inYLeftBand) return 'y-axis-left'; // Oxygen axis
      if (inYRightBand && hasFluxData) return 'y-axis-right'; // Flux axis
      if (inYRightBand) return 'y-axis-left'; // Fallback to oxygen if no flux
      if (inPlot) return 'plot';
      return 'outside';
    };

    // Helper: Apply separate scale transforms to base scales
    const computeScales = (xTrans, yTrans) => {
      return computeScalesWithBase(xTrans, yTrans, baseXScale, baseYScale);
    };

    // Helper: Apply transforms with custom base scales (for domain override during drag)
    const computeScalesWithBase = (xTrans, yTrans, baseX, baseY) => {
      const [xd0, xd1] = baseX.domain();
      const [xr0, xr1] = baseX.range();
      const newXd0 = baseX.invert((baseX(xd0) - xTrans.translate) / xTrans.k);
      const newXd1 = baseX.invert((baseX(xd1) - xTrans.translate) / xTrans.k);
      const tempXScale = d3.scaleUtc().domain([newXd0, newXd1]).range([xr0, xr1]);

      const [yd0, yd1] = baseY.domain();
      const [yr0, yr1] = baseY.range();
      const newYd0 = baseY.invert((baseY(yd0) - yTrans.translate) / yTrans.k);
      const newYd1 = baseY.invert((baseY(yd1) - yTrans.translate) / yTrans.k);
      const tempYScale = d3.scaleLinear().domain([newYd0, newYd1]).range([yr0, yr1]);

      return { tempXScale, tempYScale };
    };

    // Helper: Compute flux scale with custom base
    const computeFluxScaleWithBase = (xTrans, fluxYTrans, baseX, baseFluxY) => {
      const [xd0, xd1] = baseX.domain();
      const [xr0, xr1] = baseX.range();
      const newXd0 = baseX.invert((baseX(xd0) - xTrans.translate) / xTrans.k);
      const newXd1 = baseX.invert((baseX(xd1) - xTrans.translate) / xTrans.k);
      const tempXScale = d3.scaleUtc().domain([newXd0, newXd1]).range([xr0, xr1]);

      const [yd0, yd1] = baseFluxY.domain();
      const [yr0, yr1] = baseFluxY.range();
      const newYd0 = baseFluxY.invert((baseFluxY(yd0) - fluxYTrans.translate) / fluxYTrans.k);
      const newYd1 = baseFluxY.invert((baseFluxY(yd1) - fluxYTrans.translate) / fluxYTrans.k);
      const tempFluxYScale = d3.scaleLinear().domain([newYd0, newYd1]).range([yr0, yr1]);

      return { tempXScale, tempFluxYScale };
    };

    // Wheel zoom handler (axis-specific when over axes, even when over plot)
    // Uses D3 transitions for smooth animated zoom
    // Only allow navigation if this plot is active
    const ZOOM_TRANSITION_MS = 400; // Duration of zoom animation in milliseconds
    const ZOOM_SCALE_STEP = 1.8; // Zoom factor per wheel step (1.1 = 10% per step)

    const onWheel = (event) => {
      if (!isActive) return; // Only active plot can navigate
      event.preventDefault();
      const region = getRegion(event.clientX, event.clientY);
      if (region === 'outside') return;

      const rect = svgEl.getBoundingClientRect();
      const mouseX = event.clientX - rect.left - margin.left; // local plot coords
      const mouseY = event.clientY - rect.top - margin.top;

      const delta = -event.deltaY;
      const scaleFactor = delta > 0 ? ZOOM_SCALE_STEP : 1 / ZOOM_SCALE_STEP;

      // Use current target (if animating) or current ref values as base for new target
      const baseXTrans = zoomTargetRef.current?.x ?? { ...xTransformRef.current };
      const baseYTrans = zoomTargetRef.current?.y ?? { ...yTransformRef.current };
      const baseFluxYTrans = zoomTargetRef.current?.fluxY ?? { ...fluxYTransformRef.current };

      const newXTrans = { ...baseXTrans };
      const newYTrans = { ...baseYTrans };
      const newFluxYTrans = { ...baseFluxYTrans };

      if (region === 'x-axis') {
        // Zoom only X axis
        const oldK = newXTrans.k;
        newXTrans.k = Math.max(1, Math.min(200, oldK * scaleFactor));
        // Zoom towards mouse position
        newXTrans.translate = mouseX - (mouseX - newXTrans.translate) * (newXTrans.k / oldK);
        // Constrain translation (prevent panning left beyond start of data, allow panning right beyond end)
        const maxTx = 0; // Prevent panning left beyond start
        const minTx = plotWidth * (1 - newXTrans.k); // Prevent panning too far left when zoomed in
        // Allow negative translate values (panning right) but enforce minTx when it's positive
        if (minTx > 0) {
          newXTrans.translate = Math.max(minTx, Math.min(maxTx, newXTrans.translate));
        } else {
          // When zoomed in, minTx is negative, so only enforce maxTx (prevent left panning)
          newXTrans.translate = Math.min(maxTx, newXTrans.translate);
        }
      } else if (region === 'y-axis-left') {
        // Zoom only oxygen Y axis - keep X transform unchanged
        // Allow k to go below 1 to enable zooming out beyond initial scale (squeezing)
        const oldK = newYTrans.k;
        newYTrans.k = Math.max(0.01, Math.min(200, oldK * scaleFactor));
        // Zoom towards mouse position
        newYTrans.translate = mouseY - (mouseY - newYTrans.translate) * (newYTrans.k / oldK);
        // Constrain translation vertically
        const maxTy = 0;
        const minTy = plotHeight * (1 - newYTrans.k);
        newYTrans.translate = Math.max(minTy, Math.min(maxTy, newYTrans.translate));
      } else if (region === 'y-axis-right') {
        // Zoom only flux Y axis - keep X transform unchanged
        // Allow k to go below 1 to enable zooming out beyond initial scale (squeezing)
        const oldK = newFluxYTrans.k;
        newFluxYTrans.k = Math.max(0.01, Math.min(200, oldK * scaleFactor));
        // Zoom towards mouse position
        newFluxYTrans.translate =
          mouseY - (mouseY - newFluxYTrans.translate) * (newFluxYTrans.k / oldK);
        // Constrain translation vertically
        const maxTy = 0;
        const minTy = plotHeight * (1 - newFluxYTrans.k);
        newFluxYTrans.translate = Math.max(minTy, Math.min(maxTy, newFluxYTrans.translate));
      } else if (region === 'plot') {
        // Zoom only X axis when zooming in plot area (Y axes remain static, like flux)
        const oldKx = newXTrans.k;
        newXTrans.k = Math.max(1, Math.min(200, oldKx * scaleFactor));

        // Zoom towards mouse position (X only)
        newXTrans.translate = mouseX - (mouseX - newXTrans.translate) * (newXTrans.k / oldKx);

        // Constrain translation (prevent panning left beyond start of data, allow panning right beyond end)
        const maxTx = 0; // Prevent panning left beyond start
        const minTx = plotWidth * (1 - newXTrans.k); // Prevent panning too far left when zoomed in
        // Allow negative translate values (panning right) but enforce minTx when it's positive
        if (minTx > 0) {
          newXTrans.translate = Math.max(minTx, Math.min(maxTx, newXTrans.translate));
        } else {
          // When zoomed in, minTx is negative, so only enforce maxTx (prevent left panning)
          newXTrans.translate = Math.min(maxTx, newXTrans.translate);
        }

        // Y transforms remain unchanged (O2 and flux Y-axes stay static)
      }

      // Store the target transforms
      zoomTargetRef.current = { x: newXTrans, y: newYTrans, fluxY: newFluxYTrans };

      // Capture starting values for interpolation (current actual position)
      const startXTrans = { ...xTransformRef.current };
      const startYTrans = { ...yTransformRef.current };
      const startFluxYTrans = { ...fluxYTransformRef.current };

      // Signal that live syncing is starting (passive graph will follow)
      setIsLiveSyncing(true);

      // Helper function to render at a given interpolated state
      const renderAtTransform = (interpX, interpY, interpFluxY) => {
        // Update refs
        xTransformRef.current = interpX;
        yTransformRef.current = interpY;
        fluxYTransformRef.current = interpFluxY;

        // Use current domain override if it exists (from previous panning)
        let tempBaseYScale = baseYScale;
        if (yDomainOverrideRef.current) {
          tempBaseYScale = d3
            .scaleLinear()
            .domain(yDomainOverrideRef.current)
            .range([plotHeight, 0]);
        }

        let tempBaseFluxYScale = baseFluxYScale;
        if (fluxYDomainOverrideRef.current) {
          tempBaseFluxYScale = d3
            .scaleLinear()
            .domain(fluxYDomainOverrideRef.current)
            .range([plotHeight, 0]);
        }

        const { tempXScale, tempYScale } = computeScalesWithBase(
          interpX,
          interpY,
          baseXScale,
          tempBaseYScale,
        );
        const { tempFluxYScale } = computeFluxScaleWithBase(
          interpX,
          interpFluxY,
          baseXScale,
          tempBaseFluxYScale,
        );
        drawCanvasTrace(canvasRef.current, tempXScale, tempYScale, tempFluxYScale);

        // Update visible marks
        const [xMin, xMax] = tempXScale.domain();
        const xMinMs = xMin.getTime();
        const xMaxMs = xMax.getTime();
        const visibleMarkKeys = new Set();
        if (Array.isArray(currentMarks)) {
          currentMarks.forEach((m) => {
            const startMs = new Date(m.start).getTime();
            const endMs = new Date(m.end ?? m.start).getTime();
            if (!(endMs < xMinMs || startMs > xMaxMs)) {
              visibleMarkKeys.add(`${startMs}-${endMs}`);
            }
          });
        }
        updateMarkPositions(tempXScale, visibleMarkKeys);

        // Update event positions
        const visibleEventKeys = new Set();
        if (Array.isArray(currentEvents)) {
          currentEvents.forEach((e) => {
            const eventTime = e.time instanceof Date ? e.time : new Date(e.time);
            const eventMs = eventTime.getTime();
            if (eventMs >= xMinMs && eventMs <= xMaxMs) {
              visibleEventKeys.add(`${eventMs}`);
            }
          });
        }
        updateEventPositions(tempXScale, visibleEventKeys);

        if (updateSVGAxesRef.current) {
          updateSVGAxesRef.current(tempXScale, tempYScale, tempFluxYScale);
        }

        // Update global store for X-axis (synced between plots)
        skipNextSyncRef.current = true;
        setSyncXTransform(interpX);
      };

      // Create or reuse a D3 selection for the transition
      if (!zoomTransitionRef.current) {
        zoomTransitionRef.current = d3.select(document.createElement('div'));
      }

      // Interrupt any ongoing zoom transition and start a new one
      zoomTransitionRef.current
        .interrupt('zoom')
        .transition('zoom')
        .duration(ZOOM_TRANSITION_MS)
        .ease(d3.easeCubicOut)
        .tween('zoom', () => {
          // Create interpolators for each transform
          const interpXK = d3.interpolate(startXTrans.k, newXTrans.k);
          const interpXTx = d3.interpolate(startXTrans.translate, newXTrans.translate);
          const interpYK = d3.interpolate(startYTrans.k, newYTrans.k);
          const interpYTy = d3.interpolate(startYTrans.translate, newYTrans.translate);
          const interpFluxYK = d3.interpolate(startFluxYTrans.k, newFluxYTrans.k);
          const interpFluxYTy = d3.interpolate(startFluxYTrans.translate, newFluxYTrans.translate);

          return (t) => {
            const interpX = { k: interpXK(t), translate: interpXTx(t) };
            const interpY = { k: interpYK(t), translate: interpYTy(t) };
            const interpFluxY = { k: interpFluxYK(t), translate: interpFluxYTy(t) };

            renderAtTransform(interpX, interpY, interpFluxY);
          };
        })
        .on('end', () => {
          // Animation complete - clear target and sync final state
          zoomTargetRef.current = null;
          setIsLiveSyncing(false);

          // Final React state updates
          setXTransform(newXTrans);
          setYTransform(newYTrans);
          setFluxYTransform(newFluxYTrans);

          // Write final visible window to navigationStore (NOT graph.scaling)
          // so the dialog can read it on open without causing a flicker.
          try {
            const { k, translate } = newXTrans;
            const baseDomain = d3.extent(data, (d) => d.time);
            if (baseDomain[0] && baseDomain[1] && plotWidth > 0 && k > 0 && tabId) {
              const tempBase = d3.scaleUtc().domain(baseDomain).range([0, plotWidth]);
              const visibleMinDate = tempBase.invert(-translate / k);
              const visibleMaxDate = tempBase.invert((plotWidth - translate) / k);
              const xMinSec = visibleMinDate.getTime() / 1000;
              const xMaxSec = visibleMaxDate.getTime() / 1000;
              if (Number.isFinite(xMinSec) && Number.isFinite(xMaxSec)) {
                useNavigationStore.getState().setVisibleWindow(tabId, { xMinSec, xMaxSec });
              }
            }
          } catch {}
        })
        .on('interrupt', () => {
          // Animation was interrupted by a new wheel event
          // Don't clear target or stop syncing - the new animation will continue
        });
    };

    // Mouse drag handler for panning
    // Only allow navigation if this plot is active
    const onMouseDown = (event) => {
      if (!isActive) return; // Only active plot can navigate
      const region = getRegion(event.clientX, event.clientY);
      if (region === 'outside') return;

      event.preventDefault();
      isDraggingRef.current = true;
      setIsLiveSyncing(true); // Signal that live sync is in progress

      // Store drag start state in ref
      dragStartRef.current = {
        x: event.clientX,
        y: event.clientY,
        xTransform: { ...xTransformRef.current },
        yTransform: { ...yTransformRef.current },
        fluxYTransform: { ...fluxYTransformRef.current },
        yDomainOverride: yDomainOverrideRef.current, // Store starting Y domain override for vertical panning
        fluxYDomainOverride: fluxYDomainOverrideRef.current, // Store starting flux Y domain override
      };

      // Determine pan lock based on region
      panLockRef.current = null;
      if (region === 'x-axis') {
        panLockRef.current = 'y'; // horizontal-only pan
      } else if (region === 'y-axis-left') {
        panLockRef.current = 'x'; // vertical-only pan for oxygen
      } else if (region === 'y-axis-right') {
        panLockRef.current = 'x-flux'; // vertical-only pan for flux
      } else if (region === 'plot') {
        panLockRef.current = 'y'; // horizontal-only pan in plot area (no vertical O2 panning)
      }
    };

    const onMouseMove = (event) => {
      if (!isDraggingRef.current) return;
      event.preventDefault();

      const dx = event.clientX - dragStartRef.current.x;
      const dy = event.clientY - dragStartRef.current.y;

      const newXTrans = { ...dragStartRef.current.xTransform };
      let newYTrans = { ...dragStartRef.current.yTransform };
      let newFluxYTrans = { ...dragStartRef.current.fluxYTransform };
      let newYDomainOverride = dragStartRef.current.yDomainOverride;
      let newFluxYDomainOverride = dragStartRef.current.fluxYDomainOverride;

      // Apply pan with lock if present
      if (!panLockRef.current || panLockRef.current === 'y') {
        newXTrans.translate = dragStartRef.current.xTransform.translate + dx;
        // Constrain translation (prevent panning left beyond start of data, allow panning right beyond end)
        const maxTx = 0; // Prevent panning left beyond start
        const minTx = plotWidth * (1 - newXTrans.k); // Prevent panning too far left when zoomed in
        if (minTx > 1) {
          newXTrans.translate = Math.max(minTx, Math.min(maxTx, newXTrans.translate));
        } else {
          newXTrans.translate = Math.min(maxTx, newXTrans.translate);
        }
      }

      if (!panLockRef.current || panLockRef.current === 'x') {
        // Pan oxygen Y-axis
        // Always use domain shifting for vertical panning (allows infinite panning)
        let baseDomain;
        let tempBaseYScale;
        if (dragStartRef.current.yDomainOverride) {
          baseDomain = dragStartRef.current.yDomainOverride;
          tempBaseYScale = d3
            .scaleLinear()
            .domain(dragStartRef.current.yDomainOverride)
            .range([plotHeight, 0]);
        } else {
          baseDomain = baseYScale.domain();
          tempBaseYScale = baseYScale;
        }

        const { tempYScale } = computeScalesWithBase(
          dragStartRef.current.xTransform,
          dragStartRef.current.yTransform,
          baseXScale,
          tempBaseYScale,
        );
        const visibleDomain = tempYScale.domain();
        const visibleDomainRange = visibleDomain[1] - visibleDomain[0];
        const domainShift = (dy / plotHeight) * visibleDomainRange;

        const newDomain = [baseDomain[0] + domainShift, baseDomain[1] + domainShift];

        newYDomainOverride = newDomain;
        yDomainOverrideRef.current = newDomain;
        pendingYDomainOverrideRef.current = newDomain;
        newYTrans = { ...dragStartRef.current.yTransform };
      }

      if (panLockRef.current === 'x-flux') {
        // Pan flux Y-axis only
        let baseDomain;
        let tempBaseFluxYScale;
        if (dragStartRef.current.fluxYDomainOverride) {
          baseDomain = dragStartRef.current.fluxYDomainOverride;
          tempBaseFluxYScale = d3
            .scaleLinear()
            .domain(dragStartRef.current.fluxYDomainOverride)
            .range([plotHeight, 0]);
        } else {
          baseDomain = baseFluxYScale.domain();
          tempBaseFluxYScale = baseFluxYScale;
        }

        const { tempFluxYScale } = computeFluxScaleWithBase(
          dragStartRef.current.xTransform,
          dragStartRef.current.fluxYTransform,
          baseXScale,
          tempBaseFluxYScale,
        );
        const visibleDomain = tempFluxYScale.domain();
        const visibleDomainRange = visibleDomain[1] - visibleDomain[0];
        const domainShift = (dy / plotHeight) * visibleDomainRange;

        const newDomain = [baseDomain[0] + domainShift, baseDomain[1] + domainShift];

        newFluxYDomainOverride = newDomain;
        fluxYDomainOverrideRef.current = newDomain;
        pendingFluxYDomainOverrideRef.current = newDomain;
        newFluxYTrans = { ...dragStartRef.current.fluxYTransform };
      }

      xTransformRef.current = newXTrans;
      yTransformRef.current = newYTrans;
      fluxYTransformRef.current = newFluxYTrans;
      pendingXTransformRef.current = newXTrans;
      pendingYTransformRef.current = newYTrans;
      pendingFluxYTransformRef.current = newFluxYTrans;

      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(() => {
        // Compute scales - use domain override if it exists
        let tempBaseYScale = baseYScale;
        const domainToUse = newYDomainOverride ?? dragStartRef.current.yDomainOverride;
        if (domainToUse) {
          tempBaseYScale = d3.scaleLinear().domain(domainToUse).range([plotHeight, 0]);
        }

        let tempBaseFluxYScale = baseFluxYScale;
        const fluxDomainToUse = newFluxYDomainOverride ?? dragStartRef.current.fluxYDomainOverride;
        if (fluxDomainToUse) {
          tempBaseFluxYScale = d3.scaleLinear().domain(fluxDomainToUse).range([plotHeight, 0]);
        }

        const { tempXScale, tempYScale } = computeScalesWithBase(
          newXTrans,
          newYTrans,
          baseXScale,
          tempBaseYScale,
        );
        const { tempXScale: tempXScale2, tempFluxYScale } = computeFluxScaleWithBase(
          newXTrans,
          newFluxYTrans,
          baseXScale,
          tempBaseFluxYScale,
        );
        drawCanvasTrace(canvasRef.current, tempXScale, tempYScale, tempFluxYScale);

        // Update marks
        const [xMin, xMax] = tempXScale.domain();
        const xMinMs = xMin.getTime();
        const xMaxMs = xMax.getTime();
        const visibleMarkKeys = new Set();
        if (Array.isArray(currentMarks)) {
          currentMarks.forEach((m) => {
            const startMs = new Date(m.start).getTime();
            const endMs = new Date(m.end ?? m.start).getTime();
            if (!(endMs < xMinMs || startMs > xMaxMs)) {
              visibleMarkKeys.add(`${startMs}-${endMs}`);
            }
          });
        }
        updateMarkPositions(tempXScale, visibleMarkKeys);

        // Update event positions
        const visibleEventKeys = new Set();
        if (Array.isArray(currentEvents)) {
          currentEvents.forEach((e) => {
            const eventTime = e.time instanceof Date ? e.time : new Date(e.time);
            const eventMs = eventTime.getTime();
            if (eventMs >= xMinMs && eventMs <= xMaxMs) {
              visibleEventKeys.add(`${eventMs}`);
            }
          });
        }
        updateEventPositions(tempXScale, visibleEventKeys);

        // Throttled axis updates (only update every ~33ms to reduce flickering)
        const now = performance.now();
        const shouldUpdate = now - lastAxisUpdateRef.current >= 33;

        if (shouldUpdate) {
          if (updateSVGAxesRef.current) {
            updateSVGAxesRef.current(tempXScale, tempYScale, tempFluxYScale);
          }
          lastAxisUpdateRef.current = now;

          // Update global store for X-axis sync (like wheel zoom does)
          // skipNextSyncRef prevents this graph from reacting to its own update
          skipNextSyncRef.current = true;
          setSyncXTransform(newXTrans);
        }
      });
    };

    const onMouseUp = (event) => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      panLockRef.current = null;
      setIsLiveSyncing(false); // Signal that live sync has ended

      // Commit pending transforms to React state
      if (pendingXTransformRef.current && pendingYTransformRef.current) {
        // Update global store for X-axis (synced between plots)
        // Don't skip sync - we want active plot to read from store after drag completes
        setSyncXTransform(pendingXTransformRef.current);
        // Also update local state directly (will sync from store on next effect run)
        setXTransform(pendingXTransformRef.current);
        // Y-axes remain local
        setYTransform(pendingYTransformRef.current);
        if (pendingFluxYTransformRef.current) {
          setFluxYTransform(pendingFluxYTransformRef.current);
        }

        // Write the new visible window (in seconds) to navigationStore so the
        // dialog can read it on open. We intentionally do NOT write to
        // graph.scaling here — that would retrigger the per-chamber local-seed
        // effect and cause a one-frame flicker on passive chambers.
        try {
          const { k, translate } = pendingXTransformRef.current;
          const baseDomain = d3.extent(data, (d) => d.time);
          if (baseDomain[0] && baseDomain[1] && plotWidth > 0 && k > 0 && tabId) {
            const tempBase = d3.scaleUtc().domain(baseDomain).range([0, plotWidth]);
            const visibleMinDate = tempBase.invert(-translate / k);
            const visibleMaxDate = tempBase.invert((plotWidth - translate) / k);
            const xMinSec = visibleMinDate.getTime() / 1000;
            const xMaxSec = visibleMaxDate.getTime() / 1000;
            if (Number.isFinite(xMinSec) && Number.isFinite(xMaxSec)) {
              useNavigationStore.getState().setVisibleWindow(tabId, { xMinSec, xMaxSec });
            }
          }
        } catch {}

        pendingXTransformRef.current = null;
        pendingYTransformRef.current = null;
        pendingFluxYTransformRef.current = null;
      }

      // Commit pending Y domain overrides if we modified them during drag
      if (pendingYDomainOverrideRef.current !== null) {
        setYDomainOverride(pendingYDomainOverrideRef.current);
        pendingYDomainOverrideRef.current = null;
      }
      if (pendingFluxYDomainOverrideRef.current !== null) {
        setFluxYDomainOverride(pendingFluxYDomainOverrideRef.current);
        pendingFluxYDomainOverrideRef.current = null;
      }

      // Cursor info disabled in N-trace mode — no per-point hover readout.

      // Redraw SVG
      cancelAnimationFrame(zoomEndRafRef.current);
      zoomEndRafRef.current = requestAnimationFrame(() => {
        if (renderSVGRef.current) renderSVGRef.current();
      });
    };

    // Cursor coordinate readout (active chamber only) + mark hit-test for the
    // info tooltip. Last-known coordinates are intentionally kept on mouseleave
    // so the readout shows the most recent position; the mark tooltip clears
    // when the cursor leaves any mark's x-range.
    const onMouseMoveForCursor = (event) => {
      if (isDraggingRef.current) return;
      const region = getRegion(event.clientX, event.clientY);
      if (region !== 'plot') {
        setHoveredMark(null);
        return;
      }
      const rect = svgEl.getBoundingClientRect();
      const containerRect = containerRef.current?.getBoundingClientRect();
      const localX = event.clientX - rect.left - margin.left;
      const localY = event.clientY - rect.top - margin.top;
      const { tempXScale, tempYScale } = computeScales(
        xTransformRef.current,
        yTransformRef.current,
      );

      // Coordinate readout (active only)
      if (isActive && onCursorChange) {
        const time = tempXScale.invert(localX);
        const y1 = tempYScale.invert(localY);
        let y2 = null;
        if (hasFluxData) {
          const { tempFluxYScale } = computeFluxScaleWithBase(
            xTransformRef.current,
            fluxYTransformRef.current,
            baseXScale,
            baseFluxYScale,
          );
          if (tempFluxYScale) y2 = tempFluxYScale.invert(localY);
        }
        onCursorChange({ time, y1, y2 });
      }

      // Mark hit-test with a linger delay: tooltip only commits after the
      // cursor lingers on a mark for MARK_TOOLTIP_DELAY_MS. Moving across
      // marks resets the timer; leaving a mark cancels it. Only the active
      // chamber surfaces the tooltip — passive ones suppress it.
      if (containerRect && isActive) {
        const cursorMs = tempXScale.invert(localX).getTime();
        const marks = marksForSvgRef.current;
        let hit = null;
        if (Array.isArray(marks)) {
          for (let i = 0; i < marks.length; i++) {
            const m = marks[i];
            const startMs = (m.start instanceof Date ? m.start : new Date(m.start)).getTime();
            const endMs = (m.end instanceof Date ? m.end : new Date(m.end ?? m.start)).getTime();
            const lo = Math.min(startMs, endMs);
            const hi = Math.max(startMs, endMs);
            if (cursorMs >= lo && cursorMs <= hi) hit = m;
          }
        }

        const cx = event.clientX - containerRect.left;
        const cy = event.clientY - containerRect.top;

        if (!hit) {
          // Left all marks — clear tooltip and any pending timer.
          if (markHoverTimerRef.current) {
            clearTimeout(markHoverTimerRef.current);
            markHoverTimerRef.current = 0;
          }
          pendingHoveredMarkRef.current = null;
          setHoveredMark(null);
        } else {
          // Always update position so the visible tooltip follows the cursor.
          setHoveredMark((prev) =>
            prev && prev.mark === hit ? { mark: hit, x: cx, y: cy } : prev,
          );
          // Start (or restart) the linger timer if we're not already showing
          // this mark or pending it.
          const pending = pendingHoveredMarkRef.current;
          if (pending?.mark !== hit) {
            if (markHoverTimerRef.current) {
              clearTimeout(markHoverTimerRef.current);
            }
            pendingHoveredMarkRef.current = { mark: hit, x: cx, y: cy };
            markHoverTimerRef.current = setTimeout(() => {
              const stillPending = pendingHoveredMarkRef.current;
              if (stillPending && stillPending.mark === hit) {
                setHoveredMark(stillPending);
              }
              markHoverTimerRef.current = 0;
            }, MARK_TOOLTIP_DELAY_MS);
          } else {
            // Same mark — refresh the pending position so when the timer
            // fires it shows the latest cursor location.
            pendingHoveredMarkRef.current = { mark: hit, x: cx, y: cy };
          }
        }
      }
    };

    svgEl.addEventListener('wheel', onWheel, { passive: false });
    svgEl.addEventListener('mousedown', onMouseDown);
    svgEl.addEventListener('mousemove', onMouseMoveForCursor);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      svgEl.removeEventListener('wheel', onWheel);
      svgEl.removeEventListener('mousedown', onMouseDown);
      svgEl.removeEventListener('mousemove', onMouseMoveForCursor);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [
    plotWidth,
    plotHeight,
    baseXScale,
    baseYScale,
    baseFluxYScale,
    data,
    currentMarks,
    currentEvents,
    dimensions,
    margin,
    drawCanvasTrace,
    updateMarkPositions,
    updateEventPositions,
    setSyncXTransform,
    setIsLiveSyncing,
    isActive,
    hasFluxData,
    onCursorChange,
  ]);

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER EFFECTS - Update canvas/SVG on state changes
  // ═══════════════════════════════════════════════════════════════════════════

  // Build SVG structure when dimensions change or when new data is loaded
  // Include renderSVG and renderCanvas in deps to ensure we use the latest versions
  useEffect(() => {
    if (dimensions.width === 0 || dimensions.height === 0) return;
    // Render the SVG/canvas even when data is empty — empty extra graphs
    // should still show the time axis at the file-wide window.
    renderSVG();
    renderCanvas();
  }, [dimensions.width, dimensions.height, data, renderSVG, renderCanvas]);

  // Update canvas trace, marks, and axes on wheel zoom (drag handles updates in zoom handler)
  // Skip this effect during drag for active plot to prevent flickering
  useEffect(() => {
    if (dimensions.width === 0 || dimensions.height === 0) return;

    // Skip rendering if this is the active plot and we're currently dragging
    // The drag handler already handles rendering via requestAnimationFrame
    if (isActive && isDraggingRef.current) {
      return;
    }

    cancelAnimationFrame(rafIdRef.current);
    rafIdRef.current = requestAnimationFrame(() => {
      renderCanvas();

      // Update mark positions (only during wheel zoom - drag handles this differently)
      if (visibleMarks.length > 0) {
        const visibleSet = new Set(
          visibleMarks.map(
            (m) => `${new Date(m.start).getTime()}-${new Date(m.end ?? m.start).getTime()}`,
          ),
        );
        updateMarkPositions(xScale, visibleSet);
      }

      // Update event positions
      if (visibleEvents.length > 0) {
        const visibleEventSet = new Set(
          visibleEvents.map((e) => {
            const eventTime = e.time instanceof Date ? e.time : new Date(e.time);
            return `${eventTime.getTime()}`;
          }),
        );
        updateEventPositions(xScale, visibleEventSet);
      }

      // Update axes/grids in real-time during wheel zoom
      updateSVGAxes(xScale, yScale, fluxYScale);
    });
    return () => cancelAnimationFrame(rafIdRef.current);
  }, [
    xScale,
    yScale,
    fluxYScale,
    renderCanvas,
    updateMarkPositions,
    updateEventPositions,
    updateSVGAxes,
    dimensions.width,
    dimensions.height,
    visibleMarks,
    visibleEvents,
    isActive,
  ]);

  // Reset zoom/pan state when a new file is loaded.
  useEffect(() => {
    if (!fileIdentity) return;
    // Reset y-axis transforms + overrides on every file change. X transform
    // is intentionally NOT reset here — the per-chamber local-seed effect
    // applies the new file's graph.scaling.xMin/xMax. We only clear the
    // seeded-bounds marker so local-seed re-applies for the new file even if
    // its xMin/xMax happen to match the previous file's.
    lastSeededBoundsRef.current = null;
    setYDomainOverride(null);
    setFluxYDomainOverride(null);
    setXDomainOverride(null);
    setYTransform({ k: 1, translate: 0 });
    setFluxYTransform({ k: 1, translate: 0 });
    yTransformRef.current = { k: 1, translate: 0 };
    fluxYTransformRef.current = { k: 1, translate: 0 };
    skipNextSyncRef.current = true;
    resetSyncNavigation();
  }, [fileIdentity, resetSyncNavigation]);

  // ═══════════════════════════════════════════════════════════════════════════
  // CONTROL FUNCTIONS - Exposed to parent via ref
  // ═══════════════════════════════════════════════════════════════════════════

  const resetZoom = useCallback(() => {
    setYDomainOverride(null);
    setFluxYDomainOverride(null);
    // Reset global X-axis navigation (synced between plots)
    skipNextSyncRef.current = true;
    resetSyncNavigation();
    setXDomainOverride(null);
    setXTransform({ k: 1, translate: 0 });
    setYTransform({ k: 1, translate: 0 });
    setFluxYTransform({ k: 1, translate: 0 });
    xTransformRef.current = { k: 1, translate: 0 };
    yTransformRef.current = { k: 1, translate: 0 };
    fluxYTransformRef.current = { k: 1, translate: 0 };
  }, [resetSyncNavigation]);

  const resetXAxis = useCallback(() => {
    // Reset X to full data domain (identity transform) - preserve Y transform
    // Update global store for X-axis (synced between plots)
    const resetTransform = { k: 1, translate: 0 };
    skipNextSyncRef.current = true;
    setSyncXTransform(resetTransform);
    setSyncXDomainOverride(null);
    setXDomainOverride(null);
    setXTransform(resetTransform);
    xTransformRef.current = resetTransform;
    // Keep Y transform unchanged to preserve Y-axis zoom/pan state
    // Y domain override is also preserved to maintain Y-axis base domain
  }, [setSyncXTransform, setSyncXDomainOverride]);

  // Expose controls
  useEffect(() => {
    if (controlsRef) {
      controlsRef.current = { resetZoom, resetXAxis };
    }
  }, [controlsRef, resetZoom, resetXAxis]);

  // Loading / error / no data available
  if (isLoading) {
    return (
      <Box
        sx={{
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          backgroundColor: theme.palette.porcelain[50],
          marginLeft: `${margin.left}px`,
          marginRight: `${margin.right}px`,
          marginTop: `${margin.top}px`,
          marginBottom: `${margin.bottom}px`,
          backgroundImage: `url(${graphBackgroundPattern})`,
        }}
      >
        <CircularProgress size={24} sx={{ mb: 2 }} />
        <Typography variant="body1">Loading plot data...</Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          height: '100%',
        }}
      >
        <TriangleAlert size={24} strokeWidth={1.5} stroke={theme.palette.cranberry[600]} />
        <Typography variant="body1" sx={{ color: theme.palette.cranberry[600] }}>
          Error loading plot data
        </Typography>
      </Box>
    );
  }
  const containerSx = {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    borderRadius: 1,
    backgroundRepeat: 'repeat',
    height: height,
  };

  return (
    <Stack direction="column" spacing={1} sx={{ height: '100%' }}>
      <Box ref={containerRef} sx={containerSx}>
        {/* Bottom SVG (axes + background) */}
        <svg
          ref={svgRef}
          style={{ position: 'absolute', top: 0, left: 0, zIndex: 0, pointerEvents: 'all' }}
        />
        {/* Canvas (trace) */}
        <canvas
          ref={canvasRef}
          style={{ position: 'absolute', top: 0, left: 0, zIndex: 1, pointerEvents: 'none' }}
        />
        {hoveredMark && (
          <MarkInfoTooltip
            mark={hoveredMark.mark}
            x={hoveredMark.x}
            y={hoveredMark.y}
            containerWidth={dimensions.width}
            containerHeight={dimensions.height}
            xUnit={graph?.scaling?.xUnit}
          />
        )}
      </Box>
    </Stack>
  );
};

export default ChamberPlotView;
